import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LinkIcon from '@mui/icons-material/Link';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';

import { downloadInvitationTemplateAPI } from '../../../../services/downloadInvitationTemplate.service';
import { sendInvitationAPI, validateAudienceAPI } from '../../../../services/sendInvitation.service';
import { ButtonSendInvitation } from '../ButtonSendInvitation/ButtonSendInvitation';

import AudienceIssuesDialog from './AudienceIssuesDialog';
import { INDICATIVOS, mapBackendResult, toPayloadRows } from './audienceValidation';

import styles from './SendInvitationDialog.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\d{7,15}$/;

const ANALISIS_VACIO = {
  totalFilas: 0,
  detalle: [],
  errors: [],
  warnings: [],
  groups: [],
  cleanRows: [],
};

const emptyRow = () => ({
  id: crypto.randomUUID(),
  documento: '',
  uidEmpresa: '',
  uid2: '',
  uid3: '',
  correo: '',
  celular: '',
  indicativo: 57,
  errors: {},
});

const SendInvitationDialog = ({
  isPersonal,
  copyUrl,
  isOpen,
  visibility,
  onAudienceLoaded,
}) => {
  const [open, setOpen] = useState(false);
  const currentCompany = useSelector((state) => state.companies.currentCompany);
  const { id: surveyId } = useParams();

  const [rows, setRows] = useState([emptyRow()]);
  const [rowsError, setRowsError] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  // Solo la carga real, no la revisión del archivo: es la que puede tardar minutos.
  const [enCarga, setEnCarga] = useState(false);
  // excelFile: solo se usa en el fallback de archivos enormes, que van sin revisión previa.
  const [excelFile, setExcelFile] = useState(null);
  const [excelFileName, setExcelFileName] = useState('');
  const [issues, setIssues] = useState(null);
  const filasEnviadasRef = useRef([]);
  const excelInputRef = useRef();
  const { enqueueSnackbar } = useSnackbar();

  const handleClickOpen = () => setOpen(true);

  /** Filas con error de la última revisión, contando las que el backend recortó. */
  const totalConError = () =>
    issues?.modo === 'previo'
      ? (issues.analisisResultado?.errors?.length ?? 0) +
        (issues.resumenBackend?.fallidasOmitidas ?? 0)
      : 0;

  const revisionConErrores = () => totalConError() > 0;

  const quitarExcel = () => {
    setExcelFile(null);
    setExcelFileName('');
    // La revisión es de ese archivo: sin archivo no tiene sentido conservarla.
    setIssues((actual) => (actual?.modo === 'previo' ? null : actual));
  };

  const handleClose = () => {
    setRows([emptyRow()]);
    setRowsError('');
    setIssues(null);
    quitarExcel();
    setOpen(false);
  };

  // ── Rows helpers ──────────────────────────────────────────────
  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (id) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));

  const updateRow = (id, field, value) => {
    setRows((prev) => {
      const next = prev.map((r) =>
        r.id === id ? { ...r, [field]: value, errors: { ...r.errors } } : r
      );

      // recalcular duplicados para el campo modificado en todas las filas
      const fieldValues = next.map((r) => r[field]).filter(Boolean);
      const counts = fieldValues.reduce((acc, v) => {
        acc[v] = (acc[v] ?? 0) + 1;
        return acc;
      }, {});

      return next.map((r) => {
        const errors = { ...r.errors };
        if (field === 'correo') {
          if (r.correo && !EMAIL_RE.test(r.correo)) errors.correo = 'Correo inválido';
          else if (r.correo && counts[r.correo] > 1) errors.correo = 'Ya está repetido en otra fila';
          else errors.correo = '';
        }
        if (field === 'celular') {
          if (r.celular && !PHONE_RE.test(r.celular)) errors.celular = 'Solo dígitos (7-15)';
          else if (r.celular && counts[r.celular] > 1) errors.celular = 'Ya está repetido en otra fila';
          else errors.celular = '';
        }
        if (field === 'documento') {
          if (r.documento && counts[r.documento] > 1) errors.documento = 'Ya está repetido en otra fila';
          else errors.documento = '';
        }
        return { ...r, errors };
      });
    });
    setRowsError('');
  };

  // ── Descargar plantilla ───────────────────────────────────────
  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadInvitationTemplateAPI();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_invitacion.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar('Error al descargar la plantilla', { variant: 'error' });
    }
  };

  // ── Subir Excel ───────────────────────────────────────────────
  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      enqueueSnackbar('Solo se permiten archivos .xlsx o .xls', { variant: 'error' });
      return;
    }

    // El archivo no se abre aquí: lo revisa el backend, que es el único que valida.
    // Esta llamada NO carga nada, solo devuelve el reporte, así que responde en
    // segundos aunque el archivo traiga 20.000 filas. El operador ve los errores
    // antes de que algo quede escrito, y decide si continúa.
    setExcelFile(file);
    setExcelFileName(file.name);
    setIsLoading(true);
    try {
      const { data } = await validateAudienceAPI({ surveyId: parseInt(surveyId, 10) }, file);
      const resultado = data?.resultado ?? data?.Resultado ?? {};
      const reporte = mapBackendResult(resultado, []);

      setIssues({
        modo: 'previo',
        filas: [],
        analisisResultado: {
          ...ANALISIS_VACIO,
          ...reporte,
          detalle: [...reporte.errors, ...reporte.warnings].map((d, i) => ({
            ...d,
            key: `${d.key}#${i}`,
          })),
          totalFilas: reporte.resumen.totalFilas,
        },
        headerError: null,
        resumenBackend: reporte.resumen,
      });
    } catch (error) {
      quitarExcel();
      const detalle =
        typeof error?.response?.data === 'string' ? error.response.data : null;
      enqueueSnackbar(
        detalle ?? 'No se pudo revisar el archivo. Verifica que sea un Excel válido.',
        { variant: 'error', autoHideDuration: 6000 }
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ── Enviar ────────────────────────────────────────────────────
  /** Filas escritas a mano, en el shape que entiende audienceValidation. */
  const filasManuales = () =>
    rows
      .filter((r) => r.documento || r.uidEmpresa || r.correo || r.celular)
      .map((r, indice) => ({
        fila: indice + 1,
        origen: 'formulario',
        documento: r.documento || null,
        correo: r.correo ? r.correo.trim().toLowerCase() : null,
        celular: r.celular || null,
        indicativo: r.indicativo ?? null,
        uidEmpresa: r.uidEmpresa || null,
        uid2: r.uid2 || null,
        uid3: r.uid3 || null,
      }));

  /** POST a journey/audience con la lista ya resuelta (o con el archivo, en el fallback). */
  const ejecutarCarga = async (
    filasResueltas,
    archivo,
    omitirErrores = false,
    filasExcluidas = []
  ) => {
    setIsLoading(true);
    setEnCarga(true);
    setIssues(null);
    filasEnviadasRef.current = filasResueltas;

    const { id: companyId } = currentCompany;
    const payload = {
      surveyId: parseInt(surveyId, 10),
      companyId,
      rows: toPayloadRows(filasResueltas),
    };

    try {
      const { data } = await sendInvitationAPI(payload, archivo, omitirErrores, filasExcluidas);
      const resultado = data?.resultado ?? data?.Resultado ?? {};
      const reporte = mapBackendResult(resultado, filasEnviadasRef.current);

      // Se avisa aunque haya filas omitidas: antes solo se avisaba si todo salía
      // perfecto, y una carga de miles con algunas omitidas parecía un fracaso.
      const { procesadas } = reporte.resumen;
      if (procesadas > 0) {
        enqueueSnackbar(`Se cargaron ${procesadas} personas en la encuesta.`, {
          variant: 'success',
          autoHideDuration: 5000,
        });
        // Sin esto "Usuarios invitados" seguía en 0 hasta recargar la página.
        onAudienceLoaded?.();
      }

      if (reporte.errors.length > 0 || reporte.warnings.length > 0) {
        setIssues({
          modo: 'resultado',
          filas: [],
          analisisResultado: {
            ...ANALISIS_VACIO,
            ...reporte,
            detalle: [...reporte.errors, ...reporte.warnings].map((d, i) => ({
              ...d,
              key: `${d.key}#${i}`,
            })),
            totalFilas: reporte.resumen.totalFilas,
          },
          headerError: null,
          resumenBackend: reporte.resumen,
        });
        return;
      }

      handleClose();
    } catch (error) {
      // 409: el backend no cargó nada porque hay filas con error. Se vuelve a mostrar
      // el reporte para que se corrija el archivo o se decida omitirlas.
      if (error?.response?.status === 409) {
        const resultado =
          error.response.data?.resultado ?? error.response.data?.Resultado ?? {};
        const reporte = mapBackendResult(resultado, []);
        setIssues({
          modo: 'previo',
          filas: [],
          analisisResultado: {
            ...ANALISIS_VACIO,
            ...reporte,
            detalle: [...reporte.errors, ...reporte.warnings].map((d, i) => ({
              ...d,
              key: `${d.key}#${i}`,
            })),
            totalFilas: reporte.resumen.totalFilas,
          },
          headerError: null,
          resumenBackend: reporte.resumen,
        });
        return;
      }
      if (error?.response?.status === 400) {
        quitarExcel();
        enqueueSnackbar(
          'El archivo Excel no es válido o no contiene filas procesables. Por favor verifica el formato.',
          { variant: 'error', autoHideDuration: 4000 }
        );
      } else {
        enqueueSnackbar(
          'Error al cargar la audiencia. Intenta más tarde o comuníquese con HelpDesk',
          { variant: 'error', autoHideDuration: 2000 }
        );
      }
    } finally {
      setIsLoading(false);
      setEnCarga(false);
      setIssues((actual) => (actual?.modo === 'validacion' ? null : actual));
    }
  };

  const sendInvitation = async () => {
    setIsLoading(true);
    let hasError = false;

    // validar filas
    const filledRows = rows.filter((r) => r.documento || r.uidEmpresa || r.correo || r.celular);
    if (!excelFile && filledRows.length === 0) {
      setRowsError('Debe completar al menos una fila o subir un archivo Excel.');
      hasError = true;
    }

    // detectar duplicados entre filas
    const seenCorreo = new Map();
    const seenDocumento = new Map();
    const seenCelular = new Map();
    rows.forEach((r) => {
      if (r.correo) seenCorreo.set(r.correo, (seenCorreo.get(r.correo) ?? 0) + 1);
      if (r.documento) seenDocumento.set(r.documento, (seenDocumento.get(r.documento) ?? 0) + 1);
      if (r.celular) seenCelular.set(r.celular, (seenCelular.get(r.celular) ?? 0) + 1);
    });

    const rowsWithErrors = rows.map((r) => {
      const errors = {};
      if (r.correo && !EMAIL_RE.test(r.correo)) errors.correo = 'Correo inválido';
      else if (r.correo && seenCorreo.get(r.correo) > 1) errors.correo = 'Ya está repetido en otra fila';
      if (r.celular && !PHONE_RE.test(r.celular)) errors.celular = 'Solo dígitos (7-15)';
      else if (r.celular && seenCelular.get(r.celular) > 1) errors.celular = 'Ya está repetido en otra fila';
      if (r.documento && seenDocumento.get(r.documento) > 1) errors.documento = 'Ya está repetido en otra fila';
      return { ...r, errors };
    });

    const hasRowErrors = rowsWithErrors.some((r) => Object.values(r.errors).some(Boolean));
    if (hasRowErrors) {
      setRows(rowsWithErrors);
      hasError = true;
    }

    if (hasError) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);

    // Si el archivo ya se revisó y tiene filas con error, no se carga de largo: se
    // vuelve a mostrar la revisión para que se decida qué hacer con ellas. El backend
    // igual lo rechaza, pero aquí el operador ve por qué sin esperar la ida y vuelta.
    if (revisionConErrores()) {
      setIssues((actual) => ({ ...actual, oculto: false }));
      return;
    }

    // Todo va al backend: el archivo tal cual y las filas escritas a mano. Él revisa
    // cabecera, repetidos, documentos y correos, y devuelve el detalle por fila.
    await ejecutarCarga(filasManuales(), excelFile);
  };

  useEffect(() => {
    if (isOpen === null) return;
    setOpen(isOpen);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.SendInvitationDialog}>
      <ButtonSendInvitation
        sendIcon={<SendIcon />}
        handleClickOpen={handleClickOpen}
        visibility={visibility}
        label="Cargar audiencia"
      />

      <Dialog
        open={open}
        maxWidth="xl"
        fullWidth
        onClose={() => {
          if (!isLoading) handleClose();
        }}
      >
        <DialogTitle>Cargar audiencia</DialogTitle>
        <DialogContent>

          {enCarga && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Cargando la audiencia… Con archivos grandes puede tardar un par de minutos. No
              cierres esta ventana.
            </Alert>
          )}

          {/* ── Botones Excel ── */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              disabled={isLoading}
              size="small"
            >
              Descargar plantilla
            </Button>
            <Button
              variant="outlined"
              startIcon={excelFileName ? null : <FileUploadIcon />}
              onClick={() => excelInputRef.current.click()}
              disabled={isLoading}
              size="small"
              color={excelFileName ? 'success' : 'primary'}
            >
              {excelFileName || 'Subir Excel'}
            </Button>
            {excelFileName && (
              <Button
                size="small"
                color="error"
                onClick={quitarExcel}
              >
                Quitar archivo
              </Button>
            )}
            {issues?.modo === 'previo' && (
              // La revisión queda a la vista aunque se cierre el popup: si tiene errores
              // hay que decidir algo antes de cargar, y esconderlo lo haría fácil de pasar
              // por alto.
              <Button
                size="small"
                variant={revisionConErrores() ? 'contained' : 'outlined'}
                color={revisionConErrores() ? 'warning' : 'success'}
                onClick={() => setIssues((actual) => ({ ...actual, oculto: false }))}
                disabled={isLoading}
              >
                {revisionConErrores()
                  ? `Ver revisión: ${totalConError()} filas con error`
                  : `Revisión: ${issues.resumenBackend?.procesadas ?? 0} filas listas`}
              </Button>
            )}
            <input
              ref={excelInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleExcelUpload}
            />
          </Box>

          {/* ── Tabla de destinatarios ── */}
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>CC / Documento</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>uidempleado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 130 }}>Uid2</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 130 }}>Uid3</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>Correo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 110 }}>Indicativo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>Celular</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.documento}
                        onChange={(e) => updateRow(row.id, 'documento', e.target.value)}
                        error={!!row.errors.documento}
                        helperText={row.errors.documento}
                        placeholder="CC-123456"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.uidEmpresa}
                        onChange={(e) => updateRow(row.id, 'uidEmpresa', e.target.value)}
                        placeholder="UID empleado"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.uid2}
                        onChange={(e) => updateRow(row.id, 'uid2', e.target.value)}
                        placeholder="Uid2"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.uid3}
                        onChange={(e) => updateRow(row.id, 'uid3', e.target.value)}
                        placeholder="Uid3"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.correo}
                        onChange={(e) => updateRow(row.id, 'correo', e.target.value)}
                        error={!!row.errors.correo}
                        helperText={row.errors.correo}
                        placeholder="correo@ejemplo.com"
                        type="email"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        fullWidth
                        value={row.indicativo}
                        onChange={(e) => updateRow(row.id, 'indicativo', e.target.value)}
                      >
                        {INDICATIVOS.map((i) => (
                          <MenuItem key={i.code} value={i.code}>
                            {i.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.celular}
                        onChange={(e) => updateRow(row.id, 'celular', e.target.value)}
                        error={!!row.errors.celular}
                        helperText={row.errors.celular}
                        placeholder="3201234567"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">+{row.indicativo}</InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Eliminar fila">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => removeRow(row.id)}
                            disabled={rows.length === 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addRow}
              disabled={isLoading}
            >
              Agregar fila
            </Button>
            {rowsError && (
              <Typography variant="caption" color="error">
                {rowsError}
              </Typography>
            )}
          </Box>

          {isPersonal === false && (
            <Button
              onClick={copyUrl}
              startIcon={<LinkIcon />}
              sx={{ mt: 2 }}
            >
              Copiar enlace
            </Button>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} startIcon={<CancelIcon />} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={sendInvitation}
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            disabled={isLoading}
          >
            {isLoading ? 'Cargando…' : 'Cargar audiencia'}
          </Button>
        </DialogActions>
      </Dialog>

      {issues && (
        <AudienceIssuesDialog
          open={!issues.oculto}
          modo={issues.modo}
          filasIniciales={issues.filas}
          analisisResultado={issues.analisisResultado}
          headerError={issues.headerError}
          resumenBackend={issues.resumenBackend}
          isLoading={isLoading}
          onCorregir={() => {
            quitarExcel();
            setIssues(null);
          }}
          onConfirmar={(filasResueltas, filasExcluidas = []) =>
            // En 'previo' el archivo no se abrió en el navegador: la carga real vuelve
            // a mandarlo, junto con las filas escritas a mano. En 'validacion' llegan
            // las filas ya resueltas y no hay archivo.
            issues.modo === 'previo'
              ? ejecutarCarga(
                  filasManuales(),
                  excelFile,
                  filasResueltas === 'omitir',
                  filasExcluidas
                )
              : ejecutarCarga(filasResueltas, null)
          }
          onClose={() => {
            const eraResultado = issues.modo === 'resultado';
            if (eraResultado) {
              setIssues(null);
              handleClose();
              return;
            }
            // La revisión NO se descarta al cerrar: queda guardada y se puede volver a
            // abrir. Antes, un clic fuera del popup la borraba y no quedaba rastro de
            // que el archivo tenía errores.
            setIssues((actual) => ({ ...actual, oculto: true }));
          }}
        />
      )}
    </div>
  );
};

SendInvitationDialog.propTypes = {
  isPersonal: PropTypes.bool.isRequired,
  copyUrl: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  visibility: PropTypes.any,
  onAudienceLoaded: PropTypes.func,
};

SendInvitationDialog.defaultProps = {
  isOpen: null,
  onAudienceLoaded: null,
};

export default SendInvitationDialog;
